import React, {useEffect, useState} from 'react';
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Dropdown} from "primereact/dropdown";
import {Calendar} from "primereact/calendar";
import {InputText} from "primereact/inputtext";
import {Button} from "primereact/button";
import {Tag} from "primereact/tag";
import {Dialog} from "primereact/dialog";
import {confirmDialog, ConfirmDialog} from "primereact/confirmdialog";

import {AppContext} from "../../HomePage";
import {QueueService} from "../../service/QueueService";
import {toDateTimeString} from "../../utils/TimeUtil";

const STATE_SEVERITY = {todo: 'info', in_progress: 'warning', done: 'success', failed: 'danger'};
const STATE_CARDS = ['todo', 'in_progress', 'done', 'failed'];

// Per-state visual style for the summary cards: accent color, tinted badge
// background, and an icon. Mid-tone colors stay readable on light and dark
// surfaces; the tint is confined to the small icon badge (and selected card).
const STATE_STYLE = {
    todo:        {color: '#2563eb', bg: '#eaf1ff', icon: 'pi pi-clock'},
    in_progress: {color: '#d97706', bg: '#fff4e5', icon: 'pi pi-spin pi-spinner'},
    done:        {color: '#16a34a', bg: '#e9f8ef', icon: 'pi pi-check-circle'},
    failed:      {color: '#dc2626', bg: '#fdecec', icon: 'pi pi-times-circle'},
};

const filterLabelStyle = {fontSize: '12px', fontWeight: 500, color: 'var(--text-color-secondary)'};

const toIsoDate = (date) => {
    if (!date) return undefined;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const yesterday = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d;
};

const durationText = (row) => {
    if (!row.started_at) return '-';
    const start = new Date(row.started_at);
    const end = row.finished_at ? new Date(row.finished_at) : new Date();
    const seconds = Math.max(0, Math.round((end - start) / 1000));
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ${seconds % 60}s`;
};

const Queue = () => {
    const {t, toast, setError} = React.useContext(AppContext);
    const queueService = new QueueService();

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [counts, setCounts] = useState({todo: 0, in_progress: 0, done: 0, failed: 0, total: 0});
    const [processTypeOptions, setProcessTypeOptions] = useState([]);
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(false);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(50);
    const [detail, setDetail] = useState(null);
    const [sort, setSort] = useState({field: null, order: null});
    const [filters, setFilters] = useState({
        state: null,
        process_type: null,
        user: '',
        dates: [yesterday(), new Date()],
    });

    const stateOptions = [
        {label: t('actions.all'), value: null},
        {label: 'todo', value: 'todo'},
        {label: 'in_progress', value: 'in_progress'},
        {label: 'done', value: 'done'},
        {label: 'failed', value: 'failed'},
    ];

    const buildParams = (current, page, pageSize, sortState) => {
        const [start, end] = current.dates || [];
        const sorting = sortState && sortState.field;
        return {
            state: current.state || undefined,
            process_type: current.process_type || undefined,
            user: current.user ? current.user.trim() : undefined,
            start_date: toIsoDate(start),
            end_date: toIsoDate(end),
            sort_field: sorting ? sortState.field : undefined,
            sort_order: sorting ? sortState.order : undefined,
            page,
            page_size: pageSize,
        };
    };

    const load = (current, page, pageSize, sortState = sort) => {
        setLoading(true);
        const params = buildParams(current, page, pageSize, sortState);
        Promise.all([queueService.list(params), queueService.summary(params)])
            .then(([list, summary]) => {
                setItems(list.items || []);
                setTotal(list.total || 0);
                setCounts(summary.counts || {todo: 0, in_progress: 0, done: 0, failed: 0, total: 0});
                setProcessTypeOptions([
                    {label: t('actions.all'), value: null},
                    ...(summary.process_types || []).map(pt => ({label: pt, value: pt})),
                ]);
                setSelected([]);
            })
            .catch(err => setError(err))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load(filters, 1, rows);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const reload = (next = filters) => {
        setFirst(0);
        load(next, 1, rows, sort);
    };

    const onPage = (e) => {
        setFirst(e.first);
        setRows(e.rows);
        load(filters, (e.first / e.rows) + 1, e.rows, sort);
    };

    const onSort = (e) => {
        const next = {field: e.sortField, order: e.sortOrder};
        setSort(next);
        setFirst(0);
        load(filters, 1, rows, next);
    };

    const setFilter = (key, value) => {
        const next = {...filters, [key]: value};
        setFilters(next);
        reload(next);
    };

    const clearFilters = () => {
        const next = {state: null, process_type: null, user: '', dates: [yesterday(), new Date()]};
        const noSort = {field: null, order: null};
        setFilters(next);
        setSort(noSort);
        setFirst(0);
        load(next, 1, rows, noSort);
    };

    const cardClick = (state) => setFilter('state', filters.state === state ? null : state);

    const doRequeue = (id) => {
        confirmDialog({
            message: t('queue.confirm_requeue'),
            header: t('queue.requeue'),
            icon: 'pi pi-refresh',
            accept: () => queueService.requeue(id)
                .then(() => {
                    toast.current.show({severity: 'success', summary: t('queue.requeued')});
                    load(filters, (first / rows) + 1, rows);
                })
                .catch(err => setError(err)),
        });
    };

    const doDelete = (id) => {
        confirmDialog({
            message: t('queue.confirm_delete'),
            header: t('queue.delete'),
            icon: 'pi pi-trash',
            accept: () => queueService.remove(id)
                .then(() => {
                    toast.current.show({severity: 'success', summary: t('queue.deleted')});
                    load(filters, (first / rows) + 1, rows);
                })
                .catch(err => setError(err)),
        });
    };

    const doBulk = (op) => {
        const ids = selected.map(row => row.id);
        if (!ids.length) return;
        confirmDialog({
            message: op === 'delete' ? t('queue.confirm_delete') : t('queue.confirm_requeue'),
            header: op === 'delete' ? t('queue.bulk_delete') : t('queue.bulk_retry'),
            icon: op === 'delete' ? 'pi pi-trash' : 'pi pi-refresh',
            accept: () => queueService.bulk(op, ids)
                .then(() => {
                    toast.current.show({severity: 'success', summary: op === 'delete' ? t('queue.deleted') : t('queue.requeued')});
                    load(filters, (first / rows) + 1, rows);
                })
                .catch(err => setError(err)),
        });
    };

    const stateBody = (row) => <Tag value={row.state} severity={STATE_SEVERITY[row.state]}/>;
    const createdBody = (row) => toDateTimeString(row.created_at);
    const userBody = (row) => row.user_email || '-';
    const durationBody = (row) => durationText(row);

    const actionsBody = (row) => (
        <div className="p-d-flex" style={{gap: '0.5rem'}}>
            {row.state === 'failed' &&
                <Button icon="pi pi-refresh" className="p-button-rounded p-button-text"
                        tooltip={t('queue.retry')} onClick={() => doRequeue(row.id)}/>}
            {row.state === 'in_progress' &&
                <Button icon="pi pi-replay" className="p-button-rounded p-button-text"
                        tooltip={t('queue.reset')} onClick={() => doRequeue(row.id)}/>}
            <Button icon="pi pi-eye" className="p-button-rounded p-button-text"
                    tooltip={t('queue.detail')} onClick={() => setDetail(row)}/>
            <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-danger"
                    tooltip={t('queue.delete')} onClick={() => doDelete(row.id)}/>
        </div>
    );

    return (
        <div>
            <ConfirmDialog/>

            <div className="p-grid p-mb-3">
                {STATE_CARDS.map(state => {
                    const st = STATE_STYLE[state];
                    const selected = filters.state === state;
                    const dimmed = filters.state && !selected;
                    return (
                        <div className="p-col-6 p-md-3" key={state}>
                            <div className="card" onClick={() => cardClick(state)}
                                 title={state}
                                 style={{
                                     cursor: 'pointer',
                                     display: 'flex', alignItems: 'center', gap: '14px',
                                     background: selected ? st.bg : undefined,
                                     outline: selected ? `2px solid ${st.color}` : 'none',
                                     outlineOffset: '-2px',
                                     opacity: dimmed ? 0.55 : 1,
                                     transition: 'opacity .15s ease, background .15s ease',
                                 }}>
                                <span style={{
                                    width: '46px', height: '46px', borderRadius: '12px',
                                    background: st.bg, color: st.color,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flex: '0 0 auto',
                                }}>
                                    <i className={st.icon} style={{fontSize: '20px'}}/>
                                </span>
                                <div style={{minWidth: 0}}>
                                    <div style={{fontSize: '13px', color: 'var(--text-color-secondary)', textTransform: 'capitalize'}}>
                                        {state.replace('_', ' ')}
                                    </div>
                                    <div style={{fontSize: '26px', fontWeight: 600, lineHeight: 1.15, color: st.color}}>
                                        {(counts[state] || 0).toLocaleString()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="p-mb-3" style={{
                background: 'var(--surface-50, #f8fafc)',
                border: '1px solid var(--surface-border, #e5e7eb)',
                borderRadius: '12px',
                padding: '14px 16px',
            }}>
                <div className="p-d-flex p-flex-wrap p-ai-end" style={{gap: '0.9rem'}}>
                    <span className="p-d-flex p-flex-column" style={{gap: '5px'}}>
                        <label style={filterLabelStyle}>{t('queue.state')}</label>
                        <Dropdown value={filters.state} options={stateOptions} showClear
                                  onChange={e => setFilter('state', e.value)} style={{width: '150px'}}/>
                    </span>
                    <span className="p-d-flex p-flex-column" style={{gap: '5px'}}>
                        <label style={filterLabelStyle}>{t('queue.process_type')}</label>
                        <Dropdown value={filters.process_type} options={processTypeOptions} filter showClear
                                  onChange={e => setFilter('process_type', e.value)} style={{width: '210px'}}/>
                    </span>
                    <span className="p-d-flex p-flex-column" style={{gap: '5px'}}>
                        <label style={filterLabelStyle}>{t('queue.created')}</label>
                        <Calendar value={filters.dates} onChange={e => setFilter('dates', e.value)} showIcon
                                  selectionMode="range" dateFormat="yy-mm-dd" readOnlyInput style={{width: '230px'}}/>
                    </span>
                    <span className="p-d-flex p-flex-column" style={{gap: '5px'}}>
                        <label style={filterLabelStyle}>{t('queue.user')}</label>
                        <span className="p-input-icon-left">
                            <i className="pi pi-search"/>
                            <InputText value={filters.user} placeholder={t('queue.user_filter_placeholder')}
                                       onChange={e => setFilters({...filters, user: e.target.value})}
                                       onKeyDown={e => e.key === 'Enter' && reload()} style={{width: '210px'}}/>
                        </span>
                    </span>
                    <Button label={t('queue.refresh')} icon="pi pi-refresh" onClick={() => reload()}/>
                    <Button label={t('queue.clear')} icon="pi pi-times"
                            className="p-button-outlined p-button-secondary" onClick={clearFilters}/>
                </div>
            </div>

            {selected.length > 0 &&
                <div className="p-mb-2" style={{gap: '0.5rem', display: 'flex'}}>
                    <Button label={t('queue.bulk_retry')} icon="pi pi-refresh" className="p-button-sm p-button-outlined"
                            onClick={() => doBulk('requeue')}/>
                    <Button label={t('queue.bulk_delete')} icon="pi pi-trash" className="p-button-sm p-button-outlined p-button-danger"
                            onClick={() => doBulk('delete')}/>
                </div>}

            <DataTable value={items} loading={loading} lazy paginator
                       first={first} rows={rows} totalRecords={total} onPage={onPage}
                       rowsPerPageOptions={[25, 50, 100]}
                       sortField={sort.field} sortOrder={sort.order} onSort={onSort} removableSort
                       selection={selected} onSelectionChange={e => setSelected(e.value)}
                       dataKey="id" responsiveLayout="scroll" emptyMessage={t('queue.empty')}>
                <Column selectionMode="multiple" headerStyle={{width: '3rem'}}/>
                <Column header={t('queue.created')} body={createdBody} sortable sortField="created_at" style={{width: '160px'}}/>
                <Column field="process_type" header={t('queue.process_type')} sortable/>
                <Column header={t('queue.user')} body={userBody} sortable sortField="user_id" style={{width: '160px'}}/>
                <Column header={t('queue.state')} body={stateBody} sortable sortField="state" style={{width: '120px'}}/>
                <Column header={t('queue.duration')} body={durationBody} style={{width: '100px'}}/>
                <Column header={t('queue.actions')} body={actionsBody} style={{width: '170px'}}/>
            </DataTable>

            <Dialog header={t('queue.detail')} visible={!!detail} style={{width: '640px'}}
                    onHide={() => setDetail(null)}>
                {detail &&
                    <div>
                        <p><b>id:</b> {detail.id}</p>
                        <p><b>{t('queue.process_type')}:</b> {detail.process_type}</p>
                        <p><b>{t('queue.state')}:</b> {detail.state}</p>
                        <p><b>{t('queue.user')}:</b> {detail.user_email || '-'}</p>
                        {(detail.exception_message || detail.exception_traceback) &&
                            <div>
                                <b>{t('queue.exception')}:</b>
                                {detail.exception_message &&
                                    <pre style={{whiteSpace: 'pre-wrap', background: 'var(--surface-100)', padding: '8px'}}>{detail.exception_message}</pre>}
                                {detail.exception_traceback &&
                                    <pre style={{whiteSpace: 'pre-wrap', maxHeight: '320px', overflow: 'auto', background: 'var(--surface-100)', padding: '8px', fontSize: '12px', fontFamily: 'monospace'}}>{detail.exception_traceback}</pre>}
                            </div>}
                        <b>{t('queue.param')}:</b>
                        <pre style={{whiteSpace: 'pre-wrap', maxHeight: '300px', overflow: 'auto', background: 'var(--surface-100)', padding: '8px'}}>
                            {JSON.stringify(detail.param, null, 2)}
                        </pre>
                    </div>}
            </Dialog>
        </div>
    );
};

export default Queue;
